import { createPermissionsQueryOptions } from '@/services/rolesPermissions/rolesPermission.options'
import { createMergeTemplatePermissionsOptions } from '@/services/template/template.options'
import { checkResourceAccess, handleAccess } from '@/permissions/Permissions'
import { setupInterceptors } from '@/services/api.service'
import { redirect, useRouter } from '@tanstack/react-router'
import { useEffect, useMemo, useCallback, memo } from 'react'
import { z } from 'zod'
import { ThemeConfigurator } from '@/constants/themes/ThemeConfigurator.component'
import Content from '@/layouts/Content.layout'
import Footer from '@/layouts/Footer.layout'
import Header from '@/layouts/Header.layout'
import { applyEdgeSidebarStyles, EdgeSidebar, EdgeSidebarContent, layoutClasses, toggleTemporaryEdgeSidebar } from '@/layouts/layout-core-v6'
import { EdgeSidebarRoot } from '@/layouts/layout-core-v6/SharedEdgeSidebar'
import Root from '@/layouts/Root.layout'
import { useAuth } from '@/lib/auth'
import { ListItemText, ListItemIcon, ListItemButton, ListItem, List, Typography, Box, Divider, Paper, IconButton, useMediaQuery, Theme, Container, AlertTitle, Alert, Fab, Tooltip } from '@mui/material'
import { createFileRoute, Link, Outlet } from '@tanstack/react-router'
import { BookOpenIcon, CodeIcon, Menu, MenuIcon } from 'lucide-react'
import React from 'react'
import { useState } from 'react'
import Sidebar from '@/components/Organisms/Sidebar/Sidebar.component'
import { useQuery } from '@tanstack/react-query'
import { createUserProfileQueryOptions } from '@/services/users/userProfile.options'

export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location, }) => {
    // context.queryClient.cancelQueries()

    // console.log('beforeLoad')
    await setupInterceptors(context.queryClient)

    const isAuth = context.auth.checkAuthStatus();
    // console.warn('isAuth: ', isAuth)
    if (!isAuth) {
      console.log('Not authenticated, redirecting to login')
      context.auth.setCurrentPath(location.href)
      console.log(location.href)
      const redirectPath = `${location.href}`
      console.log('Redirect path:', redirectPath) // Debug log
      const encodedPath = encodeURIComponent(redirectPath)
      console.log('Encoded path:', encodedPath)
      throw redirect({
        to: '/login',
        search: {
          redirect: encodedPath
        },
      })
    }

    const ping = await context.auth.checkPing()

    // Stupid comment goes here
    // console.log('ping', ping)

    if (!ping) {
      const encodedPath = encodeURIComponent(location.href)
      throw redirect({
        to: '/login',
        replace: true,
        search: { redirect: encodedPath }
      })
    }

    const permissions = context.auth.permissions

    if (!permissions) {


      console.log(location)

      function sanitizePathname(pathname: string): string {
        const segments = pathname.split('/')
        if (segments.length > 1) {
          return '/' + segments[1]
        }
        return '/'
      }

      const path = sanitizePathname(location.pathname)
      console.log(path)



      const hasAccess = await checkResourceAccess({
        queryClient: context.queryClient,
        pathname: path,
        permissions,
        auth: context.auth
      })

      console.log('hasAccess', hasAccess)

      if (!hasAccess) {
        console.log('toss em out!')
        throw redirect({
          to: '/',
          replace: true,
          search: { drawer: undefined }
        })
      }
    }
  },
  loader: async ({ context }) => {
    const isAuth = context.auth.checkAuthStatus();

    const permissions = context.auth.permissions
    // console.warn('permissions', permissions)

    if (!isAuth && !permissions) {
      console.log('fire ensurequeryData')
      await Promise.all([
        setupInterceptors(context.queryClient),
        context?.queryClient?.prefetchQuery(createPermissionsQueryOptions(context.auth.checkAuthStatus)),
        context?.queryClient?.prefetchQuery(createMergeTemplatePermissionsOptions()),
        // context?.queryClient?.prefetchQuery(createRoleIdQueryOptions())
      ])

    }
  },
  component: AuthLayout,
})


function AuthLayout() {
  const [loggingOut, setLoggingOut] = useState<boolean>(false);
  const router = useRouter()
  const navigate = Route.useNavigate()
  const auth = useAuth()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const permissions = useQuery(createPermissionsQueryOptions(auth.checkAuthStatus))
  const currentUser = useQuery(createUserProfileQueryOptions(permissions?.data?.agentid))

  const handleSignOut = useCallback(async () => {
    try {
      setLoggingOut(true);
      await auth.logout();
    } catch (error) {
      console.error('Failed to store auth data:', error);
    } finally {
      setLoggingOut(false);
      navigate({ to: '/login', replace: true });
    }
  }, [auth, navigate]);


  // Memoize the navigation items to prevent recreation on each render
  const navigationItems = useMemo(() => [
    {
      to: '/',
      label: "Tickets",
      icon: <CodeIcon />,
    },
    {
      to: "/status-editor",
      label: "Status Editor",
      icon: <CodeIcon />
    },
    {
      to: "/type-editor",
      label: "Type Editor",
      icon: <BookOpenIcon />
    },
    {
      to: "/custom-fields",
      label: "Custom Fields",
      icon: <BookOpenIcon />
    },
    {
      to: "/field-groups",
      label: "Field Groups",
      icon: <CodeIcon />,
      hasDividerAfter: true,
    },
    {
      to: "/customers",
      label: "Customers",
      icon: <BookOpenIcon />
    },
    {
      to: "/users",
      label: "Users",
      icon: <BookOpenIcon />
    },
    {
      to: "/permissions",
      label: "Roles & Permissions",
      icon: <BookOpenIcon />
    },
    {
      to: "/workflow-rules",
      label: "Workflow Rules",
      icon: <BookOpenIcon />,
      hasDividerAfter: true,
    },
    {
      to: "/templates",
      label: "Templates",
      icon: <BookOpenIcon />,
      hasDividerAfter: true,
    },
    {
      to: "/reports",
      label: "Reports",
      icon: <BookOpenIcon />
    },
    {
      to: "/batch-updates",
      label: "Batch Updates",
      icon: <BookOpenIcon />,
      hasDividerAfter: true,
    },
    {
      to: "/user-profile",
      label: "User Profile",
      icon: <BookOpenIcon />,
    },
    {
      to: "/release-notes",
      label: "Release Notes",
      icon: <BookOpenIcon />
    },
    {
      to: "/sign-out",
      label: "Sign Out",
      icon: <BookOpenIcon />,
      tooltip: currentUser.data?.name || '',
      isCallback: true,
      callback: () => handleSignOut(),
    }
  ], [currentUser?.data?.name, handleSignOut]);

  const DevelopmentBanner = memo(() => {
    return (
      <Alert
        severity="warning"
        sx={{
          width: `100vw`,
          position: 'fixed',
          bottom: 0,
          borderRadius: 0,
          zIndex: 1,
        }}
      >
        <AlertTitle>Development Server</AlertTitle>
        This is a development environment.
      </Alert>
    );
  });

  useEffect(() => {
    if (!auth.isAuthenticated) {
      const redirectPath = `${router.state.location.href}`
      const encodedPath = encodeURIComponent(redirectPath)
      navigate({
        to: '/login',
        search: {
          redirect: encodedPath
        },
        replace: true
      });
    }
  }, [auth.isAuthenticated, router, navigate]);

  return (
    <>
      {isMobile ? null : <ThemeConfigurator />}
      <Root>
        {isMobile && (
          <Tooltip title={"Open Menu"} arrow>
            <Fab
              className={layoutClasses.TemporaryEdgeSidebarTrigger}
              color="primary"
              aria-label="toggle drawer"
              onClick={() => { toggleTemporaryEdgeSidebar() }}
              sx={{
                position: 'fixed', // Fixed position
                bottom: (theme: Theme) => theme.spacing(4), // 16px from the bottom
                right: (theme: Theme) => theme.spacing(2), // 16px from the left
                zIndex: (theme: Theme) => theme.zIndex.drawer + 2, // Higher than AppBar and Drawer
              }}
            >
              <MenuIcon />
            </Fab>
          </Tooltip>
        )}
        <EdgeSidebar
          sx={(theme) => ({
            ...applyEdgeSidebarStyles({
              theme,
              config: {
                xs: {
                  variant: "temporary",
                  width: "300px",
                },
                sm: {
                  variant: "permanent",
                  width: "200px",
                },
                md: {
                  variant: "permanent",
                  width: "264px",
                },
              },
            }),
            border: 'none',
          })}
        >
          <EdgeSidebarContent>
            <Sidebar items={navigationItems} />
          </EdgeSidebarContent>
        </EdgeSidebar>

        <Content>
          <Container maxWidth="none" sx={{ py: 5 }}>
            <Outlet />
          </Container>
        </Content>
        {process.env.REACT_APP_SHOW_DEV_BANNER === 'true' ? (
          <Footer
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              height: '64px',
            }}
          >
            <DevelopmentBanner />
          </Footer>
        ) : null}
      </Root>
    </>
  )
}